import { ReactNode, useCallback, useMemo, useState, useEffect } from "react";
import {
    Table,
    TableHeader,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuditLogStore, AuditLogEntry } from "@/store/audit-logs";
import { EmptyState } from "@/components/common/empty-state";
import { LoadingState } from "@/components/common/loading-state";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Eye, X, Loader2 } from "lucide-react";
import { buildApiUrl } from "@/lib/api";

const formatDate = (iso: string) => {
    try {
        return new Intl.DateTimeFormat(undefined, {
            dateStyle: "medium",
            timeStyle: "medium",
        }).format(new Date(iso));
    } catch {
        return iso;
    }
};

const ACTOR_TYPE_LABELS: Record<string, string> = {
    USER: "User",
    EVALION: "Evalion",
    STRIPE: "Stripe",
    UNKNOWN: "Unknown",
};

const formatActorType = (value?: string | null) => {
    if (!value) return "—";
    return ACTOR_TYPE_LABELS[value] ?? value;
};

export type ColumnKey =
    | "createdAt"
    | "actorType"
    | "actorIdentifier"
    | "model"
    | "action"
    | "browser";

interface ColumnDefinition {
    key: ColumnKey;
    label: string;
    minWidth?: string;
    align?: "left" | "right";
    render: (entry: AuditLogEntry) => ReactNode;
}

export const COLUMN_DEFINITIONS: ColumnDefinition[] = [
    {
        key: "createdAt",
        label: "Timestamp",
        minWidth: "min-w-[200px]",
        render: (entry) => (
            <span className="font-medium text-foreground">{formatDate(entry.createdAt)}</span>
        ),
    },
    {
        key: "actorType",
        label: "Actor Type",
        minWidth: "min-w-[120px]",
        render: (entry) => formatActorType(entry.actorType),
    },
    {
        key: "actorIdentifier",
        label: "Actor Identifier",
        minWidth: "min-w-[160px]",
        render: (entry) => (
            <span className="text-sm text-muted-foreground">{entry.actorIdentifier ?? "—"}</span>
        ),
    },
    {
        key: "model",
        label: "Model",
        minWidth: "min-w-[160px]",
        render: (entry) => (
            <span className="font-semibold text-foreground">{entry.model}</span>
        ),
    },
    {
        key: "action",
        label: "Action",
        minWidth: "min-w-[160px]",
        render: (entry) => (
            <Badge
                variant={entry.success ? "success" : "destructive"}
                className="px-2 py-0.5 text-[11px] uppercase tracking-wide"
            >
                {entry.action}
            </Badge>
        ),
    },
    {
        key: "browser",
        label: "Browser",
        minWidth: "min-w-[140px]",
        render: (entry) => (
            <span className="text-sm text-muted-foreground">{entry.browser ?? "—"}</span>
        ),
    },
];

export const DEFAULT_COLUMN_VISIBILITY: Record<ColumnKey, boolean> = COLUMN_DEFINITIONS.reduce(
    (acc, column) => {
        acc[column.key] = true;
        return acc;
    },
    {} as Record<ColumnKey, boolean>,
);

interface AuditTableProps {
    visibleColumns: Record<ColumnKey, boolean>;
}

type AuditLogDetails = AuditLogEntry & {
    targetId?: string | null;
    requestEvalionId?: string | null;
    browser?: string | null;
    before?: unknown;
    after?: unknown;
    data?: unknown;
    where?: unknown;
    meta?: unknown;
    error?: string | null;
};

export const AuditTable = ({ visibleColumns }: AuditTableProps) => {
    const entries = useAuditLogStore((state) => state.entries);
    const loading = useAuditLogStore((state) => state.loading);
    const error = useAuditLogStore((state) => state.error);
    const pageInfo = useAuditLogStore((state) => state.pageInfo);
    const pageIndex = useAuditLogStore((state) => state.pageIndex);
    const fetchEntries = useAuditLogStore((state) => state.fetchEntries);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsPanelVisible, setDetailsPanelVisible] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const [details, setDetails] = useState<AuditLogDetails | null>(null);

    const activeColumns = useMemo(
        () => COLUMN_DEFINITIONS.filter((column) => visibleColumns[column.key]),
        [visibleColumns],
    );

    const limit = pageInfo?.limit ?? 15;
    const total = pageInfo?.total;
    const hasNext = pageInfo?.hasMore ?? false;
    const hasPrevious = pageIndex > 0;

    const startRow = entries.length ? pageIndex * limit + 1 : 0;
    const endRow = entries.length ? startRow + entries.length - 1 : 0;

    const summaryText = entries.length
        ? total
            ? `Showing ${startRow.toLocaleString()}-${endRow.toLocaleString()} of ${total.toLocaleString()} records`
            : `Showing ${startRow.toLocaleString()}-${endRow.toLocaleString()} records`
        : "No records to display.";

    const handleNextPage = useCallback(async () => {
        await fetchEntries("next");
    }, [fetchEntries]);

    const handlePreviousPage = useCallback(async () => {
        await fetchEntries("prev");
    }, [fetchEntries]);

    const handleCloseDetails = useCallback(() => {
        setDetailsPanelVisible(false);
        setTimeout(() => {
            setDetailsOpen(false);
            setDetails(null);
            setDetailsError(null);
        }, 250);
    }, []);

    const handleViewDetails = useCallback(
        async (entry: AuditLogEntry) => {
            setDetailsOpen(true);
            setDetailsPanelVisible(false);
            setDetailsLoading(true);
            setDetailsError(null);
            setDetails(null);
            requestAnimationFrame(() => setDetailsPanelVisible(true));
            try {
                const response = await fetch(buildApiUrl(`/api/audit-logs/${entry.id}`), {
                    credentials: "include",
                });
                if (response.status === 401 || response.status === 403) {
                    throw new Error("You do not have access to audit log details.");
                }
                const payload = await response.json();
                if (!response.ok) {
                    throw new Error(
                        typeof payload?.message === "string"
                            ? payload.message
                            : "Unable to load audit entry details.",
                    );
                }
                setDetails({ ...payload?.data, createdAt: entry.createdAt });
            } catch (err) {
                setDetailsError(
                    err instanceof Error ? err.message : "Failed to fetch audit log details.",
                );
            } finally {
                setDetailsLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        if (!detailsOpen) {
            setDetailsPanelVisible(false);
        }
    }, [detailsOpen]);

    if (loading) {
        return (
            <div className="min-h-[320px] rounded-xl border border-border bg-card">
                <LoadingState label="Fetching audit entries…" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-xl border border-destructive/40 bg-destructive/10 px-6 py-10 text-center text-destructive">
                <h3 className="text-lg font-semibold">Unable to load audit data</h3>
                <p className="text-sm opacity-80">{error}</p>
            </div>
        );
    }

    if (!entries.length) {
        return (
            <EmptyState
                title="No audit log entries match the current filters."
                description="Adjust search terms or status filters to broaden your results."
                className="min-h-[320px]"
            />
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <Table className="min-w-full">
                    <TableHeader className="bg-muted-background/60">
                        <TableRow className="border-b border-border/70">
                            <TableHead className="w-16 border-r border-border/70 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/90">
                                #
                            </TableHead>
                            {activeColumns.map((column) => (
                                <TableHead
                                    key={column.key}
                                    className={cn(
                                        "text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/90",
                                        column.align === "right" ? "text-right" : "text-left",
                                        column.minWidth,
                                    )}
                                >
                                    {column.label}
                                </TableHead>
                            ))}
                            <TableHead className="w-[140px] text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/90">
                                Details
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-border/70">
                        {entries.map((entry, index) => (
                            <TableRow
                                key={entry.id}
                                className="bg-background/80 transition-colors hover:bg-muted/60"
                            >
                                <TableCell className="w-16 border-r border-border/70 px-4 py-3 text-sm font-medium text-primary">
                                    {startRow + index}
                                </TableCell>
                                {activeColumns.map((column) => (
                                    <TableCell
                                        key={`${entry.id}-${column.key}`}
                                        className={cn(
                                            "whitespace-nowrap px-4 py-3 text-sm text-muted-foreground",
                                            column.align === "right" ? "text-right" : "text-left",
                                        )}
                                    >
                                        {column.render(entry)}
                                    </TableCell>
                                ))}
                                <TableCell className="px-4 py-3 text-right">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground transition hover:text-primary"
                                        onClick={() => handleViewDetails(entry)}
                                        aria-label="View audit log details"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <div className="flex flex-col gap-3 border-t border-border/70 bg-card px-4 py-3 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
                    <span>{summaryText}</span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePreviousPage}
                            disabled={!hasPrevious || loading}
                            className="gap-2"
                        >
                            <ChevronLeft className="h-4 w-4" /> Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleNextPage}
                            disabled={!hasNext || loading}
                            className="gap-2"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {detailsOpen && (
                <div className="fixed inset-0 z-50 flex">
                    <div
                        className={cn(
                            "absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 z-10",
                            detailsPanelVisible ? "opacity-100" : "opacity-0",
                        )}
                        onClick={handleCloseDetails}
                        role="presentation"
                    />
                    <div
                        className={cn(
                            "relative z-20 ml-auto flex h-full w-full max-w-3xl flex-col bg-card shadow-2xl transition-transform duration-300 ease-out",
                            detailsPanelVisible ? "translate-x-0" : "translate-x-full",
                        )}
                    >
                        <div className="flex items-center justify-between border-b border-border px-6 py-4">
                            <div>
                                <h3 className="text-lg font-semibold text-primary">Audit log details</h3>
                                <p className="text-sm text-muted-foreground">
                                    {details?.model ?? ""} • {details?.action ?? ""}
                                </p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={handleCloseDetails}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            {detailsLoading && (
                                <div className="flex h-full items-center justify-center text-muted-foreground">
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Loading details…
                                </div>
                            )}
                            {!detailsLoading && detailsError && (
                                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                                    {detailsError}
                                </div>
                            )}
                            {!detailsLoading && details && (
                                <div className="space-y-6 text-sm text-muted-foreground">
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <DetailItem label="Audit ID" value={details.id} />
                                        <DetailItem label="Timestamp" value={formatDate(details.createdAt)} />
                                        <DetailItem label="Actor Type" value={formatActorType(details.actorType)} />
                                        <DetailItem label="Actor Identifier" value={details.actorIdentifier ?? "—"} />
                                        <DetailItem label="Target ID" value={details.targetId ?? "—"} />
                                        <DetailItem label="Evalion ID" value={details.requestEvalionId ?? "—"} />
                                        <DetailItem label="Browser" value={details.browser ?? "—"} />
                                        <DetailItem label="Outcome" value={details.success ? "Success" : "Failed"} />
                                    </div>
                                    {details.error && !details.success && (
                                        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                                            {details.error}
                                        </div>
                                    )}
                                    <JsonSection title="Before" value={details.before} />
                                    <JsonSection title="After" value={details.after} />
                                    <JsonSection title="Data" value={details.data} />
                                    <JsonSection title="Where" value={details.where} />
                                    <JsonSection title="Meta" value={details.meta} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const DetailItem = ({ label, value }: { label: string; value: ReactNode }) => (
    <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
            {label}
        </p>
        <p className="text-sm text-primary">{value ?? "—"}</p>
    </div>
);

const JsonSection = ({ title, value }: { title: string; value: unknown }) => {
    if (value === undefined || value === null) {
        return null;
    }

    return (
        <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
                {title}
            </h4>
            <pre className="max-h-64 overflow-x-auto whitespace-pre-wrap rounded-lg border border-border/60 bg-muted/60 p-4 text-xs leading-relaxed text-muted-foreground">
                {JSON.stringify(value, null, 2)}
            </pre>
        </div>
    );
};

